/**
 * HospitalInfoModal - Modal informativo para formato de Excel de Hospitais
 *
 * Mostra informações sobre:
 * - Colunas do Excel e seus campos correspondentes
 * - Campos obrigatórios (Nome, CNPJ/CPF)
 * - Validações aplicadas (email, CNPJ/CPF)
 * - Comportamento de sobrescrita por CNPJ
 */
import React from 'react';
import { Info, X, AlertTriangle, CheckCircle, FileSpreadsheet } from 'lucide-react';

interface HospitalInfoModalProps {
  onClose: () => void;
  onDownloadTemplate?: () => void;
}

export const HospitalInfoModal: React.FC<HospitalInfoModalProps> = ({ onClose, onDownloadTemplate }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-t-xl flex items-center justify-between sticky top-0">
          <div className="flex items-center space-x-3">
            <Info className="text-white" size={24} />
            <h3 className="text-lg font-semibold text-white">
              Formato do Excel - Importação de Hospitais
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Warning about overwrite */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Comportamento de Sobrescrita
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Se um hospital com o <strong>mesmo CNPJ/CPF</strong> já existir no sistema,
                  ele será <strong>SOBRESCRITO</strong> com os novos dados do Excel.
                </p>
              </div>
            </div>
          </div>

          {/* Required fields */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
            <p className="font-semibold text-red-800 dark:text-red-200 mb-2 flex items-center gap-2">
              <span className="text-red-500">*</span> Campos Obrigatórios
            </p>
            <ul className="text-sm text-red-700 dark:text-red-300 space-y-1 ml-4">
              <li>• <strong>Coluna C:</strong> Nome Fantasia</li>
              <li>• <strong>Coluna D:</strong> Razão Social</li>
              <li>• <strong>Coluna F:</strong> CNPJ ou CPF (identificador único)</li>
            </ul>
          </div>

          {/* Validations */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <p className="font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
              <CheckCircle size={16} /> Validações Aplicadas
            </p>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 ml-4">
              <li>• <strong>CNPJ:</strong> Formato válido (14 dígitos com verificadores)</li>
              <li>• <strong>CPF:</strong> Formato válido (11 dígitos com verificadores)</li>
              <li>• <strong>Email:</strong> Formato xxx@xxx.xxx ou xxx@xxx.com.br</li>
              <li>• <strong>Estado:</strong> Máximo 2 caracteres (UF válida)</li>
            </ul>
          </div>

          {/* Column mapping */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700 rounded-lg p-4 border-2">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <p className="mb-3">
                <span className="font-semibold text-gray-800 dark:text-gray-200">📋 Mapeamento de Colunas</span>
              </p>
              <div className="grid grid-cols-2 gap-4">
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-900 dark:text-white">A:</strong> ID <span className="text-gray-400">(ignorado)</span></li>
                  <li><strong className="text-gray-900 dark:text-white">B:</strong> Código</li>
                  <li><strong className="text-gray-900 dark:text-white">C:</strong> Nome Fantasia <span className="text-red-500 font-bold">*</span></li>
                  <li><strong className="text-gray-900 dark:text-white">D:</strong> Razão Social <span className="text-red-500 font-bold">*</span></li>
                  <li><strong className="text-gray-900 dark:text-white">E:</strong> Tipo Pessoa</li>
                  <li><strong className="text-gray-900 dark:text-white">F:</strong> CNPJ/CPF <span className="text-red-500 font-bold">*</span></li>
                  <li><strong className="text-gray-900 dark:text-white">G:</strong> Inscrição Estadual</li>
                  <li><strong className="text-gray-900 dark:text-white">H:</strong> Inscrição Municipal</li>
                  <li><strong className="text-gray-900 dark:text-white">I:</strong> Endereço</li>
                  <li><strong className="text-gray-900 dark:text-white">J:</strong> Bairro</li>
                  <li><strong className="text-gray-900 dark:text-white">K:</strong> Cidade</li>
                  <li><strong className="text-gray-900 dark:text-white">L:</strong> Estado (UF)</li>
                </ul>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-900 dark:text-white">M:</strong> CEP</li>
                  <li><strong className="text-gray-900 dark:text-white">N:</strong> Contato</li>
                  <li><strong className="text-gray-900 dark:text-white">O:</strong> Telefone</li>
                  <li><strong className="text-gray-900 dark:text-white">P:</strong> Ramal</li>
                  <li><strong className="text-gray-900 dark:text-white">Q:</strong> Email</li>
                  <li><strong className="text-gray-900 dark:text-white">R:</strong> Periodicidade</li>
                  <li><strong className="text-gray-900 dark:text-white">S:</strong> Observações</li>
                  <li><strong className="text-gray-900 dark:text-white">T:</strong> Reagendamento</li>
                  <li><strong className="text-gray-900 dark:text-white">U:</strong> Ativo <span className="text-gray-400">(Sim/Não/S/N)</span></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Template info removed as per user request */}

          {/* Footer */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
